import React, { useState, useCallback, useMemo, useContext } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb/BreadCrumb";
import TableContainerMin from "../../../Components/Common/TableContainerMin";
import { useQuery, useQueryClient } from "react-query";
import { DeleteOutlined, EditOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Modal, Spin } from "antd";
import HistoryContext from "../../../Components/Hooks/HistoryContext";
import toast from "react-hot-toast";
import { deleteRole, getRoles } from "../services/roleApi";
import "../../Styles/style.css"
import AddEditRoleForm from "../component/AddEditRole";


const Roles = () => {
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();

  document.title = `Roles | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;

  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetRoleName, setDeleteTargetRoleName] = useState("");
  
  const { isLoading, isError, error, data: role } = useQuery("roles", getRoles);

  const toggleAddEditModal = useCallback((role = null) => {
    setSelectedRole(role);
    setIsAddEditModalOpen(prev => !prev);
    if (isDetailModalOpen) setIsDetailModalOpen(false);
  }, [isDetailModalOpen]);

  const handleEdit = (role) => {
    toggleAddEditModal(role);
  };

  const toggleDetailModal = useCallback((role = null) => {
    setSelectedRole(role);
    setIsDetailModalOpen(prev => !prev);
    if (isAddEditModalOpen) setIsAddEditModalOpen(false);
  }, [isAddEditModalOpen]);

  const handleDetail = (role) => {
    toggleDetailModal(role);
  };

  const handleDelete = async (roleId) => {
    try {
      await deleteRole(roleId);
      queryClient.invalidateQueries("roles");
      toast.success("Le chauffeur a été supprimée avec succès 🎉");
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error(`😔 Erreur lors de la suppression du chauffeur : ${error?.message||error}`);
    } finally {
      setDeleteConfirmModal(false);
    }
  };

  const openDeleteConfirmModal = (roleId) => {
    const roleToDelete = role.find(role => role.id === roleId);
    if (roleToDelete) {
      setDeleteTargetId(roleId);
      setDeleteTargetRoleName(roleToDelete.name);
      setDeleteConfirmModal(true);
    }
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModal(false);
  };

  const columns = useMemo(() => [
    { Header: "Numéro", accessor: "id", filterable: false },
    { Header: "Nom", accessor: "name", filterable: false },
    { Header: "Description", accessor: "description", filterable: false },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button
            title="Modifier"
            size="sm"
            onClick={() => handleEdit(row.original)}
            className="btn btn-soft-warning"
          >
            <EditOutlined />
          </Button>
          <Button
            title="Supprimer"
            size="sm"
            onClick={() => openDeleteConfirmModal(row.original.id)}
            className="btn btn-soft-danger"
          >
            <DeleteOutlined />
          </Button>
        </div>
      ),
      filterable: false,
    },
  ], [handleDetail, handleEdit, openDeleteConfirmModal]);


  const HeaderTable = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex gap-3">
        Liste des roles de mon application
      </div>
        <Button
          type="button"
          className="active-tab"
          icon={<SettingOutlined /> }
          title="Ajout role"
          onClick={() => toggleAddEditModal(null)}
          style={{ marginRight: '20px' }}
        >
          {" "} Ajouter un role
        </Button>
    </div>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page="roles" history={history} />
          <Row>
            <Col>
              <Card id="contactList">
                <CardHeader>
                  <HeaderTable />
                </CardHeader>
                <CardBody className="pt-0">
                  <TableContainerMin
                    columns={columns}
                    data={role || []}
                    isGlobalSearch={true}
                    isGlobalFilter={true}
                    customPageSize={10}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light"
                    SearchPlaceholder="Recherche..."
                    HeaderTable={HeaderTable}
                  />
                  {isLoading && !isError && (
                    <div className="text-center">
                      Chargement, Merci de patienter ...  <Spin />
                    </div>
                  )}
                  {isError && (
                    <div className="text-center">
                      Erreur : {error?.message || error}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          <AddEditRoleForm
            modal={isAddEditModalOpen}
            setModal={setIsAddEditModalOpen}
            toggle={toggleAddEditModal}
            selectedRole={selectedRole}
          />

          <Modal
            title="Confirmer la suppression de cet utilisateur"
            open={deleteConfirmModal}
            onCancel={closeDeleteConfirmModal}
            footer={[
              <Button key="cancel" onClick={closeDeleteConfirmModal} className="btn btn-soft-primary">
                Annuler
              </Button>,
              <Button key="delete" type="danger" className="btn btn-soft-danger" onClick={() => handleDelete(deleteTargetId)}>
                Oui, Supprime
              </Button>,
            ]}
          >
            <p>Êtes-vous sûr de vouloir supprimer le role {deleteTargetRoleName} ?</p>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Roles;
